import { Box, Link, Typography } from '@mui/material';

import { numericSx } from '../../../theme/typography';
import { useCurrency } from '../../../contexts/CurrencyContext';
import type { BillingWidget } from '../../shared/billing';
import { PanelCard } from './PanelCard';
import { StatTile } from './StatTile';

interface BillingWidgetCardProps {
  data: BillingWidget | undefined;
  loading?: boolean;
  onOpenReports?: () => void;
}

const MINI_BAR_HEIGHT = 76;

function monthShortLabel(monthISO: string): string {
  const [y, m] = monthISO.split('-').map(Number);
  if (!y || !m) return monthISO;
  return new Date(Date.UTC(y, m - 1, 1)).toLocaleDateString('en-GB', {
    month: 'short',
    timeZone: 'UTC',
  });
}

/**
 * Dashboard `Billing` widget (payment milestones PM-23, AC-16) — billed this
 * month (with a trend vs the previous month), awaiting payment, and a
 * next-3-months scheduled mini-chart.
 *
 * Laid out as a full-width tile grid (the `FinancialsSection` idiom) rather
 * than the mock-up's single compact card: on the real dashboard every section
 * spans the content column, and a lone 480px card left the rest of its row
 * empty at tablet and desktop widths (phase-3 sign-off feedback).
 *
 * Reads `widgets.billing` from the dashboard summary the shell already
 * fetched — **no request of its own** (AC-16 names this explicitly). Renders
 * null when there is nothing to show and loading has finished, so a caller
 * without `view:reports:financial` — for whom the block is absent from the
 * payload, never zeroed — sees nothing here even if the section gate is ever
 * bypassed.
 *
 * Naming discipline: "Billed" / "Awaiting payment" / "Scheduled" — never
 * "Revenue" (SPEC §Visual design language).
 */
export function BillingWidgetCard({ data, loading, onOpenReports }: BillingWidgetCardProps) {
  const { formatCurrency } = useCurrency();

  if (!loading && !data) return null;

  const billedThisMonth = data?.billedThisMonth ?? 0;
  // The server sends no growth over a £0 prior month, so the pill is omitted
  // rather than showing a fabricated ±∞%/0%.
  const trendPct = data?.growthPct ?? null;
  const prevMonthLabel = new Date(
    new Date().getFullYear(),
    new Date().getMonth() - 1,
    1,
  ).toLocaleDateString('en-GB', { month: 'short' });

  const months = data?.next3Months ?? [];
  const scheduledTotal = data?.scheduledTotal ?? 0;
  const maxScheduled = Math.max(...months.map((m) => m.scheduled), 1);

  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
        gap: 2,
        alignItems: 'stretch',
      }}
      data-testid="widget-billing"
    >
      <StatTile
        label="Billed this month"
        value={formatCurrency(billedThisMonth)}
        sub={`vs ${prevMonthLabel}`}
        trend={trendPct == null ? undefined : { value: trendPct }}
        loading={loading}
        data-testid="stat-billed-this-month"
      />
      <StatTile
        label="Awaiting payment"
        value={formatCurrency(data?.awaitingPayment ?? 0)}
        sub="Invoiced, not yet paid"
        loading={loading}
        data-testid="stat-awaiting-payment"
      />

      {months.length > 0 && (
        <PanelCard
          title="Scheduled · next 3 months"
          action={
            onOpenReports ? (
              <Link
                component="button"
                type="button"
                onClick={onOpenReports}
                underline="hover"
                sx={{ fontSize: '0.8125rem' }}
              >
                Forecast →
              </Link>
            ) : undefined
          }
        >
          <Box sx={{ display: 'flex', alignItems: 'flex-end', height: MINI_BAR_HEIGHT, gap: '10px' }}>
            {months.map((m) => {
              const heightPct = maxScheduled > 0 ? (m.scheduled / maxScheduled) * 100 : 0;
              return (
                <Box
                  key={m.monthISO}
                  sx={{
                    flex: 1,
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'flex-end',
                    alignItems: 'center',
                  }}
                  title={`${monthShortLabel(m.monthISO)}: ${formatCurrency(m.scheduled)} scheduled`}
                >
                  <Box
                    sx={(theme) => ({
                      width: '100%',
                      height: `${Math.max(heightPct, m.scheduled > 0 ? 4 : 0)}%`,
                      bgcolor: theme.palette.bars[1].fill,
                      borderRadius: '4px 4px 0 0',
                    })}
                  />
                </Box>
              );
            })}
          </Box>
          <Box sx={{ display: 'flex', gap: '10px', mt: '4px', borderTop: '1px solid', borderColor: 'divider', pt: '4px' }}>
            {months.map((m) => (
              <Typography
                key={m.monthISO}
                component="div"
                sx={[numericSx, { flex: 1, textAlign: 'center', fontSize: '0.7rem', color: 'text.disabled' }]}
              >
                {monthShortLabel(m.monthISO)}
              </Typography>
            ))}
          </Box>
          <Typography
            component="div"
            sx={[numericSx, { fontSize: '0.71875rem', color: 'text.disabled', mt: 1 }]}
          >
            {`Next ${months.length} month${months.length === 1 ? '' : 's'} scheduled · ${formatCurrency(scheduledTotal)}`}
          </Typography>
        </PanelCard>
      )}
    </Box>
  );
}

export default BillingWidgetCard;
